import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";

import AppDialog from "components/common/AppDialog";

export type CaptchaChallenge = {
  task_id: number;
  status: string;
  image_base64?: string;
  image_width?: number;
  image_height?: number;
  instruction?: string;
  task_type?: string;
  expected_clicks?: number | null;
  round_num?: number;
  expires_at?: string;
};

type Props = {
  open: boolean;
  challenge: CaptchaChallenge | null;
  submitting: boolean;
  error: string | null;
  canSubmit: boolean;
  onSubmit: (clicks: number[][], taskType: string) => void;
  onClose: () => void;
};

/**
 * The operator solves the bot's captcha here. Points are reported in pixels of
 * the screenshot the bot sent, so clicks are scaled back up from the rendered
 * size.
 */
export default function CaptchaSolveDialog({
  open,
  challenge,
  submitting,
  error,
  canSubmit,
  onSubmit,
  onClose,
}: Props) {
  const [points, setPoints] = useState<number[][]>([]);
  const [dragMode, setDragMode] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const round = challenge?.round_num ?? 1;
  const detectedType = challenge?.task_type || "";
  const waitingForNext =
    open && (!challenge?.image_base64 || challenge?.status !== "pending");

  useEffect(() => {
    setPoints([]);
    setDragMode(detectedType === "drag");
  }, [round, challenge?.status, detectedType]);

  useEffect(() => {
    if (!open || !challenge?.expires_at) {
      setSecondsLeft(null);
      return undefined;
    }
    const deadline = Date.parse(challenge.expires_at);
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [open, challenge?.expires_at]);

  const imageSrc = useMemo(
    () =>
      challenge?.image_base64
        ? `data:image/png;base64,${challenge.image_base64}`
        : "",
    [challenge?.image_base64],
  );

  const handleImageClick = (event: MouseEvent<HTMLImageElement>) => {
    const image = imgRef.current;
    if (!image || !canSubmit || submitting) return;
    const rect = image.getBoundingClientRect();
    const scaleX = (image.naturalWidth || rect.width) / rect.width;
    const scaleY = (image.naturalHeight || rect.height) / rect.height;
    setPoints((prev) => [
      ...prev,
      [
        Math.round((event.clientX - rect.left) * scaleX),
        Math.round((event.clientY - rect.top) * scaleY),
      ],
    ]);
  };

  const expected = challenge?.expected_clicks ?? null;
  const isDrag = dragMode;
  // A drag is replayed as pairs; an odd count would drag to nowhere.
  const incompleteDrag = isDrag && points.length % 2 !== 0;

  const title = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <span>Solve the captcha</span>
      <Chip size="small" label={`Round ${round}`} />
      {secondsLeft != null && (
        <Chip
          size="small"
          color={secondsLeft < 30 ? "error" : "warning"}
          label={`${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")} left`}
        />
      )}
    </Stack>
  );

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseIcon
      title={title}
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="contained"
            disabled={
              !points.length ||
              submitting ||
              !canSubmit ||
              waitingForNext ||
              incompleteDrag
            }
            onClick={() => onSubmit(points, isDrag ? "drag" : "click-tiles")}
          >
            {submitting ? "Sending..." : "Send"}
          </Button>
        </>
      }
    >
      {waitingForNext ? (
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Answer sent - waiting for the bot. Paycom usually serves a few
            challenges in a row, the next one will appear here.
          </Typography>
        </Stack>
      ) : (
        <>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {challenge?.instruction || "Click the matching tiles"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {isDrag
              ? "Drag task: click the piece first, then the spot it belongs to - two clicks per move, in that order."
              : expected
                ? `Click exactly ${expected} place(s) in the image below.`
                : "Click every matching place in the image below."}
          </Typography>

          <FormControlLabel
            sx={{ mb: 1 }}
            control={
              <Switch
                size="small"
                checked={dragMode}
                onChange={(event) => {
                  setDragMode(event.target.checked);
                  setPoints([]);
                }}
                disabled={submitting || !canSubmit}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Move / drag puzzle (clicks count as pairs)
              </Typography>
            }
          />

          <Box
            sx={{ position: "relative", display: "inline-block", maxWidth: "100%" }}
          >
            <Box
              component="img"
              ref={imgRef}
              src={imageSrc}
              alt="captcha challenge"
              onClick={handleImageClick}
              sx={{
                display: "block",
                maxWidth: "100%",
                cursor: canSubmit ? "crosshair" : "not-allowed",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                userSelect: "none",
              }}
            />
            {points.map((point, index) => {
              const image = imgRef.current;
              const naturalW = image?.naturalWidth || challenge?.image_width || 1;
              const naturalH = image?.naturalHeight || challenge?.image_height || 1;
              return (
                <Box
                  key={`${point[0]}-${point[1]}-${index}`}
                  sx={{
                    position: "absolute",
                    left: `${(point[0] / naturalW) * 100}%`,
                    top: `${(point[1] / naturalH) * 100}%`,
                    transform: "translate(-50%, -50%)",
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    bgcolor: "warning.main",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    pointerEvents: "none",
                    boxShadow: 2,
                  }}
                >
                  {isDrag
                    ? `${Math.floor(index / 2) + 1}${index % 2 === 0 ? "a" : "b"}`
                    : index + 1}
                </Box>
              );
            })}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{ mt: 1.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              {isDrag
                ? `${Math.floor(points.length / 2)} move(s) marked`
                : `${points.length} point(s) marked`}
            </Typography>
            <Button
              size="small"
              onClick={() => setPoints((prev) => prev.slice(0, -1))}
              disabled={!points.length || submitting}
            >
              Undo
            </Button>
            <Button
              size="small"
              onClick={() => setPoints([])}
              disabled={!points.length || submitting}
            >
              Clear
            </Button>
          </Stack>

          {incompleteDrag ? (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Now click where that piece should go.
            </Alert>
          ) : null}
          {error ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {error}
            </Alert>
          ) : null}
        </>
      )}
    </AppDialog>
  );
}
